/** The landing page's small shared pieces: reveal-on-scroll, section shells, the WhatsApp button. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@client/lib/utils";
import { wa } from "@client/lib/sales";

export const scrollTo = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

/** True once the element has been on screen (`once`), or while it is (`once: false`). */
export function useInView<T extends HTMLElement>(opts: { once?: boolean; margin?: string } = {}) {
  const { once = true, margin = "0px 0px -10% 0px" } = opts;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver(([entry]) => {
      const visible = Boolean(entry?.isIntersecting);
      setInView(visible);
      if (visible && once) io.disconnect();
    }, { rootMargin: margin });
    io.observe(el);
    return () => io.disconnect();
  }, [once, margin]);
  return { ref, inView };
}

/**
 * Rises into view the first time it is seen. Visible by default: only `.site.js-ready` hides an
 * unseen one (styles/site.css), so the text is there for crawlers and previews.
 */
export function Reveal({ children, className, delay = 0, as = "div" }: {
  children: ReactNode; className?: string; delay?: number; as?: "div" | "li";
}) {
  const { ref, inView } = useInView<HTMLDivElement & HTMLLIElement>();
  const Tag = as;
  return (
    <Tag ref={ref} data-shown={inView} className={cn("l-reveal", className)}
         style={delay ? { animationDelay: `${delay}ms` } : undefined}>
      {children}
    </Tag>
  );
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn("l-eyebrow mb-4", className)}>{children}</p>;
}

/** A page section on one of the two surfaces, with the standard gutters and width. */
export function Section({ id, tone, className, inner, children, label }: {
  id?: string; tone: "dark" | "light"; className?: string; inner?: string; children: ReactNode; label?: string;
}) {
  return (
    <section id={id} aria-label={label}
             className={cn("relative scroll-mt-20 px-4 py-20 md:px-6 md:py-28", tone === "light" && "l-light", className)}>
      <div className={cn("relative mx-auto max-w-6xl", inner)}>{children}</div>
    </section>
  );
}

export function WhatsAppButton({ text, children, className, variant = "primary" }: {
  text: string; children: ReactNode; className?: string; variant?: "primary" | "ghost" | "ink";
}) {
  return (
    <a href={wa(text)} target="_blank" rel="noopener noreferrer"
       className={cn("inline-flex items-center justify-center gap-2 rounded-full font-bold",
                     variant === "primary" && "l-cta",
                     variant === "ghost" && "border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10",
                     variant === "ink" && "bg-[var(--l-ink)] text-white transition-colors hover:bg-black",
                     className)}>
      {children}
    </a>
  );
}

/** The WhatsApp glyph, so the buttons say where they go before anyone reads them. */
export function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={cn("h-5 w-5", className)} fill="currentColor">
      <path d="M12.04 2a9.9 9.9 0 0 0-8.53 14.94L2 22l5.2-1.36A9.9 9.9 0 1 0 12.04 2Zm0 18.13a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.09.81.83-3.01-.2-.31a8.22 8.22 0 1 1 6.95 3.84Zm4.5-6.15c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.16.25-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42h-.48c-.16 0-.43.06-.66.31-.23.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.16 1.73 2.64 4.2 3.7.59.25 1.05.4 1.4.52.59.19 1.13.16 1.55.1.47-.07 1.46-.6 1.66-1.18.21-.58.21-1.08.14-1.18-.06-.1-.22-.16-.47-.29Z" />
    </svg>
  );
}
